import { useState } from 'react';
import { Card, Button, Badge, Disclosure, FormSelect } from '@/components/UI';
import DataTable from '@/components/UI/DataTable';
import { FaChartBar, FaSync, FaClock } from 'react-icons/fa';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import type { GroupedJobs } from './types';
import ObjectiveModeControl, { type ObjectiveMode, type RealizedOutcomeMaterializationEntityType } from './ObjectiveModeControl';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface JobStatusOverviewProps {
    availableDurations: string[];
    availableHorizons: number[];
    experimentId: number;
    groupedJobs: GroupedJobs;
    isQueueJobPending: boolean;
    materializationEntityType?: RealizedOutcomeMaterializationEntityType | null;
    objectiveMode: ObjectiveMode;
    realizedOutcomeSetId: number | null;
    strategyId?: number | null;
    onObjectiveModeChange: (mode: ObjectiveMode) => void;
    onRealizedOutcomeSetIdChange: (id: number | null) => void;
    onRetryJob: (duration: string, horizonSeconds: number) => void;
    onStartAnalysis: () => void;
    onViewAnalysis: (duration: string, horizonSeconds: number) => void;
    selectedDuration: string;
    selectedHorizon: number;
    setSelectedDuration: (duration: string) => void;
    setSelectedHorizon: (horizonSeconds: number) => void;
}

const JobStatusOverview = ({
    availableDurations,
    availableHorizons,
    experimentId,
    groupedJobs,
    isQueueJobPending,
    materializationEntityType = null,
    objectiveMode,
    realizedOutcomeSetId,
    strategyId = null,
    onObjectiveModeChange,
    onRealizedOutcomeSetIdChange,
    onRetryJob,
    onStartAnalysis,
    onViewAnalysis,
    selectedDuration,
    selectedHorizon,
    setSelectedDuration,
    setSelectedHorizon
}: JobStatusOverviewProps) => {
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
        completed: false,
        active: false,
        failed: true
    });

    const realizedOutcomeSetMissing = objectiveMode === 'strategy_exit' && realizedOutcomeSetId === null;

    // Handler for toggling section collapse
    const toggleSection = (section: 'completed' | 'active' | 'failed') => {
        setCollapsedSections((prev) => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <div className="space-y-6">
            {/* Completed Analyses */}
            {groupedJobs.completed.length > 0 && (
                <Card className="bg-zinc-900 p-4">
                    <Disclosure
                        expanded={!collapsedSections.completed}
                        headerActions={<Badge variant="success">{groupedJobs.completed.length}</Badge>}
                        title={<h4 className="text-lg font-semibold text-white">✅ Completed Analyses ({groupedJobs.completed.length})</h4>}
                        variant="plain"
                        onToggle={() => toggleSection('completed')}
                    >
                        <DataTable
                            striped
                            columns={[
                                {
                                    accessorKey: 'duration',
                                    header: 'Duration',
                                    size: 100,
                                    cell: ({ row }) => <span className="text-white font-medium">{row.original.duration}</span>
                                },
                                {
                                    accessorKey: 'horizonSeconds',
                                    header: 'Horizon',
                                    size: 100,
                                    cell: ({ row }) => (
                                        <span className="text-zinc-300">{formatTimeHorizon(row.original.horizonSeconds)}</span>
                                    )
                                },
                                {
                                    id: 'entriesAnalyzed',
                                    header: 'Entries',
                                    size: 100,
                                    cell: ({ row }) => (
                                        <span className="text-zinc-400">{row.original.analysis?.entriesAnalyzed || '-'}</span>
                                    )
                                },
                                {
                                    id: 'completedAt',
                                    header: 'Completed',
                                    size: 150,
                                    cell: ({ row }) => {
                                        const date = row.original.job?.completedAt || row.original.analysis?.createdAt;

                                        return date ? (
                                            <span className="text-zinc-400 text-sm">{dayjs(date).format('MMM D, h:mm A')}</span>
                                        ) : (
                                            <span className="text-zinc-500">-</span>
                                        );
                                    }
                                },
                                {
                                    id: 'actions',
                                    header: '',
                                    size: 100,
                                    cell: ({ row }) => (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() => onViewAnalysis(row.original.duration, row.original.horizonSeconds)}
                                        >
                                            View
                                        </Button>
                                    )
                                }
                            ]}
                            data={groupedJobs.completed}
                            pagination={{ enabled: groupedJobs.completed.length > 10, pageSize: 10 }}
                            search={false}
                        />
                    </Disclosure>
                </Card>
            )}

            {/* Running/Pending Jobs */}
            {groupedJobs.active.length > 0 && (
                <Card className="bg-zinc-900 p-4">
                    <Disclosure
                        expanded={!collapsedSections.active}
                        headerActions={<Badge variant="warning">{groupedJobs.active.length}</Badge>}
                        title={<h4 className="text-lg font-semibold text-white">⏳ Running/Pending Jobs ({groupedJobs.active.length})</h4>}
                        variant="plain"
                        onToggle={() => toggleSection('active')}
                    >
                        <DataTable
                            striped
                            columns={[
                                {
                                    accessorKey: 'duration',
                                    header: 'Duration',
                                    size: 100,
                                    cell: ({ row }) => <span className="text-white font-medium">{row.original.duration}</span>
                                },
                                {
                                    accessorKey: 'horizonSeconds',
                                    header: 'Horizon',
                                    size: 100,
                                    cell: ({ row }) => (
                                        <span className="text-zinc-300">{formatTimeHorizon(row.original.horizonSeconds)}</span>
                                    )
                                },
                                {
                                    id: 'status',
                                    header: 'Status',
                                    size: 120,
                                    cell: ({ row }) => {
                                        const status = row.original.job?.status;

                                        return status === 'running' ? (
                                            <Badge variant="warning">
                                                <FaSync className="animate-spin mr-1 inline" size={10} />
                                                Running
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary">
                                                <FaClock className="mr-1 inline" size={10} />
                                                Queued
                                            </Badge>
                                        );
                                    }
                                },
                                {
                                    id: 'startedAt',
                                    header: 'Started',
                                    size: 150,
                                    cell: ({ row }) => {
                                        const date = row.original.job?.startedAt;

                                        return date ? (
                                            <span className="text-zinc-400 text-sm">{dayjs(date).fromNow()}</span>
                                        ) : (
                                            <span className="text-zinc-500">-</span>
                                        );
                                    }
                                }
                            ]}
                            data={groupedJobs.active}
                            pagination={{ enabled: false, pageSize: 10 }}
                            search={false}
                        />
                    </Disclosure>
                </Card>
            )}

            {/* Failed Jobs */}
            {groupedJobs.failed.length > 0 && (
                <Card className="bg-zinc-900 p-4">
                    <Disclosure
                        expanded={!collapsedSections.failed}
                        headerActions={<Badge variant="danger">{groupedJobs.failed.length}</Badge>}
                        title={<h4 className="text-lg font-semibold text-white">❌ Failed Jobs ({groupedJobs.failed.length})</h4>}
                        variant="plain"
                        onToggle={() => toggleSection('failed')}
                    >
                        <DataTable
                            striped
                            columns={[
                                {
                                    accessorKey: 'duration',
                                    header: 'Duration',
                                    size: 100,
                                    cell: ({ row }) => <span className="text-white font-medium">{row.original.duration}</span>
                                },
                                {
                                    accessorKey: 'horizonSeconds',
                                    header: 'Horizon',
                                    size: 100,
                                    cell: ({ row }) => (
                                        <span className="text-zinc-300">{formatTimeHorizon(row.original.horizonSeconds)}</span>
                                    )
                                },
                                {
                                    id: 'error',
                                    header: 'Error',
                                    size: 300,
                                    cell: ({ row }) => (
                                        <span className="text-red-400 text-sm truncate block" title={row.original.job?.errorMessage || ''}>
                                            {row.original.job?.errorMessage || 'Unknown error'}
                                        </span>
                                    )
                                },
                                {
                                    id: 'actions',
                                    header: '',
                                    size: 100,
                                    cell: ({ row }) => (
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => onRetryJob(row.original.duration, row.original.horizonSeconds)}
                                        >
                                            Retry
                                        </Button>
                                    )
                                }
                            ]}
                            data={groupedJobs.failed}
                            pagination={{ enabled: false, pageSize: 10 }}
                            search={false}
                        />
                    </Disclosure>
                </Card>
            )}

            {/* Start New Analysis */}
            <Card className="bg-zinc-900 p-4">
                <h4 className="text-lg font-semibold text-white mb-3">➕ Start New Analysis</h4>
                <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
                    <FormSelect
                        noMargin
                        className="w-32"
                        label="Duration"
                        options={availableDurations.map((duration) => ({
                            value: duration,
                            label: duration
                        }))}
                        value={selectedDuration}
                        onChange={(e) => setSelectedDuration(e.target.value)}
                    />
                    <FormSelect
                        noMargin
                        className="w-40"
                        label="Time Horizon"
                        options={availableHorizons.map((horizon) => ({
                            value: horizon.toString(),
                            label: formatTimeHorizon(horizon)
                        }))}
                        value={selectedHorizon.toString()}
                        onChange={(e) => setSelectedHorizon(parseInt(e.target.value))}
                    />
                    <ObjectiveModeControl
                        requireOutcomeSet
                        disabled={isQueueJobPending}
                        duration={selectedDuration}
                        experimentId={experimentId}
                        horizonSeconds={selectedHorizon}
                        materializationEntityType={materializationEntityType}
                        objectiveMode={objectiveMode}
                        realizedOutcomeSetId={realizedOutcomeSetId}
                        selectId="parameter-discovery-start-realized-outcome-set"
                        strategyId={strategyId}
                        onObjectiveModeChange={onObjectiveModeChange}
                        onRealizedOutcomeSetIdChange={onRealizedOutcomeSetIdChange}
                    />
                    <Button
                        className="inline-flex items-center"
                        disabled={isQueueJobPending || realizedOutcomeSetMissing}
                        size="sm"
                        variant="primary"
                        onClick={onStartAnalysis}
                    >
                        <FaChartBar className="mr-2" />
                        {isQueueJobPending ? 'Starting...' : 'Start Analysis'}
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default JobStatusOverview;
